import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { visibleTimelineRanges, type TrackingTimeline } from "../tracking/player/timeline-status";

/** The row under the pointer is independent of the annotation currently pinned. */
export interface TimelineRowHover {
  id: string;
  area: "label" | "canvas";
}

export interface TimelineStore {
  // UI State
  pixelsPerSecond: number;
  collapsedCollections: Set<string>;
  selectedSegmentId: string | null;
  highlightedSegmentIds: string[];
  /** Exact canvas highlight; video hover previews require a current-frame match to this segment. */
  hoveredTimelineSegmentId: string | null;
  hoveredTimelineSegmentRange: { start: number; end: number } | null;
  /** Stable GT segments and their frame availability, published atomically in bounded batches. */
  trackingTimeline: TrackingTimeline | null;
  hoveredTimelineRow: TimelineRowHover | null;
  isScrubbing: boolean;
  isMinimized: boolean;

  // Actions
  setPixelsPerSecond: (pixelsPerSecond: number) => void;
  toggleCollapsed: (collectionId: string) => void;
  selectSegment: (segmentId: string | null) => void;
  toggleSegment: (segmentId: string) => void;
  setHighlightedSegmentIds: (segmentIds: string[]) => void;
  setTrackingTimeline: (timeline: TrackingTimeline) => void;
  clearTrackingTimeline: (expected: TrackingTimeline) => void;
  /** `focusId=null` keeps hover visual-only and does not focus a video annotation. */
  setHoveredTimelineSegment: (
    segmentId: string,
    focusId: string | null,
    range?: { start: number; end: number },
  ) => void;
  clearHoveredTimelineSegment: (segmentId: string, range?: { start: number; end: number }) => void;
  setHoveredTimelineRow: (rowId: string, area: TimelineRowHover["area"]) => void;
  clearHoveredTimelineRow: (rowId: string, area: TimelineRowHover["area"]) => void;
  setIsScrubbing: (isScrubbing: boolean) => void;
  setIsMinimized: (isMinimized: boolean) => void;

  // Getters
  isCollapsed: (collectionId: string) => boolean;
}

export const createTimelineStore = (initialPixelsPerSecond = 50) => {
  return create<TimelineStore>()(
    devtools(
      (set, get) => ({
        // Initial state
        pixelsPerSecond: initialPixelsPerSecond,
        collapsedCollections: new Set(),
        selectedSegmentId: null,
        highlightedSegmentIds: [],
        hoveredTimelineSegmentId: null,
        hoveredTimelineSegmentRange: null,
        hoveredTimelineRow: null,
        isScrubbing: false,
        isMinimized: false,

        trackingTimeline: null,

        // Actions
        setTrackingTimeline: (trackingTimeline) => {
          const state = get();
          if (state.trackingTimeline === trackingTimeline) return;
          const range = state.hoveredTimelineSegmentRange;
          const envelope = state.hoveredTimelineSegmentId
            ? trackingTimeline.segments.get(state.hoveredTimelineSegmentId)
            : undefined;
          const next =
            range && envelope
              ? visibleTimelineRanges(envelope).find(
                  (visible) => visible.start <= range.start && visible.end >= range.end,
                )
              : undefined;
          // Keep an intact piece hovered as it grows, but clear a split bar's
          // preview: its old range no longer identifies the piece under the pointer.
          const stillVisible = !range || next !== undefined;
          set({
            trackingTimeline,
            ...(range && next && stillVisible && (next.start !== range.start || next.end !== range.end)
              ? { hoveredTimelineSegmentRange: { start: next.start, end: next.end } }
              : {}),
            ...(!stillVisible
              ? {
                  hoveredTimelineSegmentId: null,
                  hoveredTimelineSegmentRange: null,
                  highlightedSegmentIds: [],
                }
              : {}),
          });
        },
        clearTrackingTimeline: (expected) => {
          if (get().trackingTimeline === expected) set({ trackingTimeline: null });
        },
        setPixelsPerSecond: (pixelsPerSecond) => set({ pixelsPerSecond }),

        toggleCollapsed: (collectionId) =>
          set((state) => {
            const next = new Set(state.collapsedCollections);
            if (next.has(collectionId)) {
              next.delete(collectionId);
            } else {
              next.add(collectionId);
            }
            return { collapsedCollections: next };
          }),

        selectSegment: (segmentId) => {
          const state = get();
          if (
            state.selectedSegmentId === segmentId &&
            (segmentId !== null ||
              (!state.highlightedSegmentIds.length &&
                state.hoveredTimelineSegmentId === null &&
                state.hoveredTimelineRow === null))
          ) {
            return;
          }
          set({
            selectedSegmentId: segmentId,
            ...(segmentId === null
              ? {
                  highlightedSegmentIds: [],
                  hoveredTimelineSegmentId: null,
                  hoveredTimelineSegmentRange: null,
                  hoveredTimelineRow: null,
                }
              : {}),
          });
        },

        toggleSegment: (segmentId) => {
          const state = get();
          set({
            selectedSegmentId: state.selectedSegmentId === segmentId ? null : segmentId,
            highlightedSegmentIds: [],
            hoveredTimelineSegmentId: null,
            hoveredTimelineSegmentRange: null,
          });
        },

        setHighlightedSegmentIds: (segmentIds) => {
          // Prevent redundant updates to break potential sync loops
          const { highlightedSegmentIds: current, hoveredTimelineSegmentId, hoveredTimelineRow } = get();
          const unchanged = current.length === segmentIds.length && current.every((id, i) => id === segmentIds[i]);
          if (unchanged && hoveredTimelineSegmentId === null && hoveredTimelineRow === null) {
            return;
          }
          set({
            highlightedSegmentIds: unchanged ? current : segmentIds,
            hoveredTimelineSegmentId: null,
            hoveredTimelineSegmentRange: null,
            hoveredTimelineRow: null,
          });
        },

        setHoveredTimelineRow: (rowId, area) => {
          const state = get();
          if (state.isScrubbing) return;
          if (state.hoveredTimelineRow?.id === rowId && state.hoveredTimelineRow.area === area) return;
          const ids = area === "label" ? [rowId] : [];
          const unchanged =
            state.highlightedSegmentIds.length === ids.length &&
            state.highlightedSegmentIds.every((id, i) => id === ids[i]);
          set({
            hoveredTimelineRow: { id: rowId, area },
            hoveredTimelineSegmentId: null,
            hoveredTimelineSegmentRange: null,
            highlightedSegmentIds: unchanged ? state.highlightedSegmentIds : ids,
          });
        },

        clearHoveredTimelineRow: (rowId, area) => {
          const hover = get().hoveredTimelineRow;
          // A late canvas/label leave must not clear the newer surface's hover.
          if (hover?.id !== rowId || hover.area !== area) return;
          set({
            hoveredTimelineRow: null,
            hoveredTimelineSegmentId: null,
            hoveredTimelineSegmentRange: null,
            highlightedSegmentIds: [],
          });
        },

        setHoveredTimelineSegment: (segmentId, focusId, range) => {
          const { highlightedSegmentIds: current, hoveredTimelineSegmentId, hoveredTimelineSegmentRange } = get();
          const nextHighlighted = focusId === null ? [] : [focusId];
          const unchanged =
            current.length === nextHighlighted.length && current.every((id, index) => id === nextHighlighted[index]);
          if (
            unchanged &&
            hoveredTimelineSegmentId === segmentId &&
            hoveredTimelineSegmentRange?.start === range?.start &&
            hoveredTimelineSegmentRange?.end === range?.end
          )
            return;
          set({
            highlightedSegmentIds: unchanged ? current : nextHighlighted,
            hoveredTimelineSegmentId: segmentId,
            hoveredTimelineSegmentRange: range ?? null,
            hoveredTimelineRow: focusId === null ? null : { id: focusId, area: "canvas" },
          });
        },

        clearHoveredTimelineSegment: (segmentId, range) => {
          // Konva can deliver the old bar's leave after a DOM label's enter.
          // Only the bar that still owns the hover may clear the preview.
          if (get().hoveredTimelineSegmentId !== segmentId) return;
          const currentRange = get().hoveredTimelineSegmentRange;
          if (range && (currentRange?.start !== range.start || currentRange?.end !== range.end)) return;
          set({ highlightedSegmentIds: [], hoveredTimelineSegmentId: null, hoveredTimelineSegmentRange: null });
        },

        setIsScrubbing: (isScrubbing) => {
          if (get().isScrubbing !== isScrubbing) {
            set({ isScrubbing });
          }
        },

        setIsMinimized: (isMinimized) => {
          if (get().isMinimized !== isMinimized) {
            set({ isMinimized });
          }
        },

        // Getters
        isCollapsed: (collectionId) => get().collapsedCollections.has(collectionId),
      }),
      { name: "timelineStore" },
    ),
  );
};
